import React, { useCallback, useEffect, useMemo, useRef, type SyntheticEvent } from 'react';
import { styled } from '@compiled/react';
import type { UIAnalyticsEvent } from '@atlaskit/analytics-next';
import GrowDiagonalIcon from '@atlaskit/icon/core/grow-diagonal';
import ShrinkDiagonalIcon from '@atlaskit/icon/core/shrink-diagonal';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip';
import { Box, Inline, xcss } from '@atlaskit/primitives';
import type { ActiveKeyMap } from '@atlassian/jira-common-components-keyboard-shortcuts/src/shortcut-registry.tsx';
import Shortcuts from '@atlassian/jira-common-components-keyboard-shortcuts/src/shortcuts/index.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { FormattedMessage, useIntl } from '@atlassian/jira-intl';
import { useIsFullscreen } from '@atlassian/jira-layout-controller/src/controllers/layout-controller/consumers/fullscreen/index.tsx';
import { fireUIAnalytics, type Attributes } from '@atlassian/jira-product-analytics-bridge';
import { ThemedButton } from '@atlassian/jira-project-theme-components/src/ui/themed-button/ThemedButton.tsx';
import { ThemedIconButton } from '@atlassian/jira-project-theme-components/src/ui/themed-button-new/ThemedButtonNew.tsx';

import messages from './messages.tsx';

type Props = {
	testId: string;
	/** @deprecated refs are not needed for fullscreen in nav4 */
	getCustomRefs: () => (HTMLElement | null)[];
	analyticsAttributes: Attributes;
	onToggle: (isFullScreen: boolean, globalStyle?: HTMLElement) => void;
	withContainerStyles?: boolean;
	defaultFullscreen?: boolean;
	/** @deprecated controlled mode is not supported with nav4 */
	isFullscreen: boolean;
	shouldRenderContent?: boolean;
	isSubtle?: boolean;
	isHidden?: boolean;
	hasShortcut?: boolean;
};

const removeFullScreenStyle = (customRefs: null | Array<HTMLElement | null>) => {
	// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
	document.getElementById('fullscreen-global-style')?.remove();
	if (customRefs) {
		customRefs.forEach((ref) => {
			if (ref !== null) {
				ref.classList.remove('fullscreen');
			}
		});
	}
};

let globalStyle: HTMLElement | null = null;

const FullscreenButton = ({
	testId,
	onToggle,
	getCustomRefs,
	analyticsAttributes: extraAnalyticsAttributes,
	withContainerStyles = true,
	defaultFullscreen = false,
	shouldRenderContent = true,
	isSubtle = false,
	isHidden,
	hasShortcut = true,
}: Props) => {
	const { formatMessage } = useIntl();
	const customRefElements = useRef<(HTMLElement | null)[] | null>(null);
	const [isFullscreenLayout, { setIsFullscreen: setIsFullScreenLayout }] = useIsFullscreen();

	if (!__SERVER__) {
		globalStyle = document.createElement('style');
	}

	const isFullscreen = isFullscreenLayout;
	const handleKeysInFullScreen = useCallback((e: KeyboardEvent) => {
		// Ignore if it looks like the user is typing something
		if (
			e.target instanceof HTMLElement &&
			(['INPUT', 'TEXTAREA', 'SELECT'].includes(e.target.tagName.toUpperCase()) ||
				e.target.hasAttribute('contenteditable'))
		) {
			return;
		}

		// Prevent shortcuts
		const shortcutKeysToPrevent = [
			'/', // search
			'h', // help
		];
		if (shortcutKeysToPrevent.includes(e.key)) {
			e.stopPropagation();
		}
	}, []);

	useEffect(() => {
		if (!hasShortcut) {
			return;
		}

		if (isFullscreen) {
			// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
			window?.addEventListener('keydown', handleKeysInFullScreen, true);
		} else {
			// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
			window?.removeEventListener('keydown', handleKeysInFullScreen, true);
		}

		// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
		return () => window?.removeEventListener('keydown', handleKeysInFullScreen, true);
	}, [isFullscreen, handleKeysInFullScreen, hasShortcut]);

	const createFullScreenStyle = useCallback(() => {
		const customElements = customRefElements.current;
		const hiddenStyles =
			"#ak-jira-navigation, #ak-side-navigation, #right-sidebar-panel-wrapper, [data-skip-link-wrapper='true'] { display: none }";

		// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
		const hiddenStylesNode = document.createTextNode(hiddenStyles);

		if (globalStyle) {
			globalStyle.id = 'fullscreen-global-style';
			globalStyle.appendChild(hiddenStylesNode);
		}

		if (customElements) {
			customElements.forEach((ref: HTMLElement | null) => {
				if (ref !== null) {
					ref.classList.add('fullscreen');
				}
			});
		}

		if (globalStyle) {
			// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
			document.head?.appendChild(globalStyle);
		}
	}, []);

	const onClick = useCallback(
		(event: SyntheticEvent, analyticsEvent: UIAnalyticsEvent) => {
			const analyticsAttributes: Attributes = {
				...extraAnalyticsAttributes,
				expandToFullscreen: !isFullscreenLayout,
			};

			fireUIAnalytics(analyticsEvent, 'fullscreenButton', analyticsAttributes);
			setIsFullScreenLayout(!isFullscreenLayout);
			onToggle(!isFullscreenLayout);
		},
		[extraAnalyticsAttributes, isFullscreenLayout, onToggle, setIsFullScreenLayout],
	);

	useEffect(() => {
		customRefElements.current = getCustomRefs();
		const customElements = customRefElements.current;

		if (isFullscreen) {
			createFullScreenStyle();
		} else {
			removeFullScreenStyle(customElements);
		}

		return () => removeFullScreenStyle(customElements);
	}, [createFullScreenStyle, getCustomRefs, isFullscreen]);

	useEffect(() => {
		if (defaultFullscreen) {
			createFullScreenStyle();
		}
		// Empty dependency array to only apply fullscreen styling once (on mount).

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const renderButtonWithTooltip = useCallback(() => {
		if (fg('jira-full-screen')) {
			const getFullscreenButtonLabel = () => {
				if (isFullscreen) {
					return hasShortcut
						? formatMessage(messages.exitFullScreenShortcutNew, {
								inline: (parts: React.ReactNode[]) => (
									<Inline xcss={keyboardShortcutTagStyles}>{parts}</Inline>
								),
							})
						: formatMessage(messages.exitFullScreen);
				}

				return hasShortcut
					? formatMessage(messages.enterFullScreenShortcutNew, {
							inline: (parts: React.ReactNode[]) => (
								<Inline xcss={keyboardShortcutTagStyles}>{parts}</Inline>
							),
						})
					: formatMessage(messages.enterFullScreen);
			};

			return (
				<ThemedIconButton
					{...(withContainerStyles
						? { testId }
						: { testId: 'platform.ui.fullscreen-button.fullscreen-button' })}
					label={getFullscreenButtonLabel()}
					onClick={onClick}
					icon={isFullscreen ? ShrinkDiagonalIcon : GrowDiagonalIcon}
					appearance={isSubtle ? 'subtle' : 'default'}
					isTooltipDisabled={false}
					aria-keyshortcuts={
						hasShortcut ? formatMessage(messages.fullScreenShortcutNewPlain) : undefined
					}
					aria-hidden={isHidden}
					tabIndex={isHidden ? -1 : 0}
				/>
			);
		}

		return (
			<Tooltip
				content={
					<Box xcss={KeyboardShortcutContainer}>
						{formatMessage(isFullscreen ? messages.exitFullScreen : messages.enterFullScreen)}
						{hasShortcut && (
							<Box xcss={keyboardShortcutContainerStyles}>
								<FormattedMessage
									{...messages.fullScreenShortcut}
									values={{
										inline: (parts: React.ReactNode[]) => (
											<Inline xcss={keyboardShortcutTagStyles}>{parts}</Inline>
										),
									}}
								/>
							</Box>
						)}
					</Box>
				}
				position="top"
			>
				<ThemedButton
					{...(withContainerStyles
						? { testId }
						: { testId: 'platform.ui.fullscreen-button.fullscreen-button' })}
					onClick={onClick}
					aria-keyshortcuts={
						hasShortcut ? formatMessage(messages.fullScreenShortcutPlain) : undefined
					}
					iconBefore={
						isFullscreen ? (
							<ShrinkDiagonalIcon
								label={formatMessage(messages.exitFullScreen)}
								spacing="spacious"
								color={token('color.icon.subtle')}
							/>
						) : (
							<GrowDiagonalIcon
								label={formatMessage(messages.enterFullScreen)}
								spacing="spacious"
								color={token('color.icon.subtle')}
							/>
						)
					}
					appearance={isSubtle ? 'subtle' : 'default'}
					aria-hidden={isHidden}
					tabIndex={isHidden ? -1 : 0}
				/>
			</Tooltip>
		);
	}, [
		formatMessage,
		isFullscreen,
		isHidden,
		isSubtle,
		onClick,
		testId,
		withContainerStyles,
		hasShortcut,
	]);

	const keyMapOld: ActiveKeyMap = useMemo(
		() => ({
			z: {
				callback: () => {
					setIsFullScreenLayout(!isFullscreenLayout);
				},
			},
		}),
		[isFullscreenLayout, setIsFullScreenLayout],
	);

	const keyMap: ActiveKeyMap = useMemo(
		() => ({
			Z: {
				callback: () => {
					setIsFullScreenLayout(!isFullscreenLayout);
				},
			},
		}),
		[isFullscreenLayout, setIsFullScreenLayout],
	);

	if (!shouldRenderContent) {
		return null;
	}

	return withContainerStyles ? (
		<FullscreenButtonContainer data-testid="platform.ui.fullscreen-button.fullscreen-button">
			{renderButtonWithTooltip()}
			{hasShortcut && <Shortcuts keyMap={fg('jira-full-screen') ? keyMap : keyMapOld} />}
		</FullscreenButtonContainer>
	) : (
		<>
			{renderButtonWithTooltip()}
			{hasShortcut && <Shortcuts keyMap={fg('jira-full-screen') ? keyMap : keyMapOld} />}
		</>
	);
};

export default FullscreenButton;

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const FullscreenButtonContainer = styled.div({
	display: 'inline-flex',
	backgroundColor: token('elevation.surface.overlay'),
	boxShadow: token('elevation.shadow.overlay'),
	// eslint-disable-next-line @atlaskit/design-system/no-unsafe-design-token-usage -- The token value "4px" and fallback "3px" do not match and can't be replaced automatically.
	borderRadius: token('radius.small', '3px'),
});

const KeyboardShortcutContainer = xcss({
	display: 'flex',
	lineHeight: 'space.200',
	alignItems: 'center',
	paddingBottom: 'space.025',
	paddingTop: 'space.025',
});

const keyboardShortcutTagStyles = xcss({
	display: 'inline-flex',
	paddingLeft: 'space.050',
	paddingRight: 'space.050',
	justifyContent: 'center',
	alignItems: 'center',
	gap: 'space.075',
	color: 'color.text.inverse',
	borderColor: 'color.border.inverse',
	borderRadius: 'radius.small',
	borderWidth: 'border.width',
	borderStyle: 'solid',
	textAlign: 'center',
});

const keyboardShortcutContainerStyles = xcss({
	display: 'flex',
	alignItems: 'center',
	marginLeft: 'space.050',
	columnGap: 'space.050',
});
